<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.vehicle_repair_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddVehicleRepair">
                {{ $t('labels.vehicleRepairAdd') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            :headers="tableHeaders"
            :server="apiLists"
            :use-archive="apiArchive"
            use-selection
            store-name="vehicleDataTable"
            store-key="VehicleRepair" />
    </div>

    <RepairAdd
        ref="repairAdd"
        @refreshTables="refreshTables" />

    <RepairDetail
        ref="repairDetail"
        @refreshTables="refreshTables" />

    <RepairEdit
        ref="repairEdit"
        @refreshTables="refreshTables" />

    <hts-modal
        :show="showPopupDelete"
        title="Xóa sửa chữa xe"
        type="confirm"
        content="Bạn có muốn xóa dữ liệu sửa chữa xe này không ?"
        @hide="closePopupDelete"
        @confirm="() => actionDelete()" />
</template>

<script setup lang="ts">
import Actions from './ignore/actions.vue'
import RepairAdd from './ignore/repair/add.vue'
import RepairDetail from './ignore/repair/detail.vue'
import RepairEdit from './ignore/repair/edit.vue'
import LinkDocument from './ignore/repair/link-document.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'
const { $formatters } = useNuxtApp()

const apiArchive = computed(() => {
  return { api: $url.vehicle.repair.archive, permissions: checkPermission(layoutPermissions.vehicle_repair_archive) }
})

const apiLists = $url.vehicle.repair.getList

const repairAdd = ref()
const repairDetail = ref()
const repairEdit = ref()
const dataTables = ref()
const showPopupDelete = ref<boolean>(false)
const isSelectedToDelete = ref('')

const handleAddVehicleRepair = () => {
  repairAdd.value && repairAdd.value.open()
}

const refreshTables = () => {
  dataTables.value && dataTables.value.refresh()
}

const handleViewDetail = (data: any) => {
  if (!data) {
    return null
  }

  if (repairDetail.value) {
    repairDetail.value.open(data)
  }
}
const handleEdit = (data: any) => {
  if (!data) {
    return null
  }

  if (repairEdit.value) {
    repairEdit.value.open(data)
  }
}
const handleDelete = (data: any) => {
  if (!data) {
    return null
  }
  showPopupDelete.value = true
  isSelectedToDelete.value = data._id
}

const closePopupDelete = () => {
  showPopupDelete.value = false
}

const actionDelete = $lodash.debounce(() => {
  const api = $url.vehicle.repair.delete
  $api(api, { body: { id: isSelectedToDelete.value } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      process.client && $toast().success(resData.data)
      closePopupDelete()
      refreshTables()
    }
  })
}, 500)

const tableHeaders = [
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicle'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'repairDate',
    headerName: $t('labels.repairDate'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueFormatter: (params: any) => {
      return params.data.repairDate ? $formatters.date(params.data.repairDate) : ''
    }
  },
  {
    field: 'repairType',
    headerName: $t('labels.repairType'),
    valueFormatter: (params: any) => {
      const repairType = params.data.repairType
      if (repairType === 'MAJOR_REPAIR') {
        return 'Sửa chữa lớn'
      }
      if (repairType === 'MINOR_REPAIR') {
        return 'Sửa chữa nhỏ'
      }
      return ''
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: 'Sửa chữa lớn', value: 'MAJOR_REPAIR', class: { '!bg-green-100': true, '!text-green-800': true } },
      { name: 'Sửa chữa nhỏ', value: 'MINOR_REPAIR', class: { '!bg-green-300': true, '!text-green-800': true } }
    ],
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    field: 'repairCost',
    headerName: $t('labels.repairCost'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueFormatter: (params: any) => {
      return params.data.repairCost ? $formatters.formatCurrency(params.data.repairCost) : 0
    }
  },
  {
    field: 'repairAgencyAbbreviationName',
    headerName: $t('labels.repairAgency'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      return params.data.repairAgencyAbbreviationName
    }
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true,
    filter: 'agTextColumnFilter',
    autoHeight: true,
    valueFormatter: (params: any) => {
      return params.data.description
    }
  },
  {
    field: 'document',
    headerName: $t('labels.document'),
    sortable: true,
    cellRenderer: LinkDocument,
    autoHeight: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'VehicleRepair',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          click: handleViewDetail,
          permissions: checkPermission(layoutPermissions.vehicle_repair_management),
          isAll: true
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_repair_edit),
          click: handleEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.vehicle_repair_delete),
          click: handleDelete,
          isArchive: true
        }
      ]
    }
  }
]
</script>
